import { createContext, useReducer, useCallback } from "react";

import axios from "../../utils/axios/v1/userAxios";

const SET_DOMAINS = "SET_DOMAINS";
const SET_LOADING = "SET_LOADING";
const SET_ERROR = "SET_ERROR";

const initialState = {
  domains: [],
  loading: false,
  errMsg: null,
};

const DomainReducer = (state, action) => {
  switch (action.type) {
    case SET_DOMAINS:
      return {
        ...state,
        domains: action.payload.domains,
      };
    case SET_LOADING:
      return {
        ...state,
        loading: action.payload.loading,
      };
    case SET_ERROR:
      return {
        ...state,
        errMsg: action.payload.errMsg,
      };
    default:
      return state;
  }
};

const DomainContext = createContext(null);

function DomainProvider({ children }) {
  const [state, dispatch] = useReducer(DomainReducer, initialState);

  const setDomains = useCallback((domains) => {
    dispatch({
      type: SET_DOMAINS,
      payload: { domains },
    });
  }, []);

  const setLoading = useCallback((loading) => {
    dispatch({
      type: SET_LOADING,
      payload: { loading },
    });
  }, []);

  const setErr = useCallback((errMsg) => {
    dispatch({
      type: SET_ERROR,
      payload: { errMsg },
    });
  }, []);

  const getDomains = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get("/organisation/domains");
      setDomains(response.data || []);
      setErr(null);
    } catch (err) {
      setDomains([]);
      setErr(err.message || "Failed to fetch domains");
    } finally {
      setLoading(false);
    }
  }, [setDomains, setErr, setLoading]);

  const getDomain = useCallback(async (domain) => {
    try {
      const response = await axios.get(`/organisation/domains/${domain}`);
      return response.data;
    } catch (err) {
      setErr(err.message || "Failed to fetch domain");
      return null;
    }
  }, [setErr]);

  const addDomain = useCallback(async (domain) => {
    setLoading(true);
    try {
      const response = await axios.post("/organisation/domains", { domain });
      await getDomains();
      return response.data;
    } catch (err) {
      setErr(err.message || "Failed to add domain");
      throw err;
    } finally {
      setLoading(false);
    }
  }, [getDomains, setErr, setLoading]);

  const verifyDomain = useCallback(async (domain) => {
    setLoading(true);
    try {
      const response = await axios.post(`/organisation/domains/${domain}/verify`);
      await getDomains();
      return response.data;
    } catch (err) {
      setErr(err.message || "Failed to verify domain");
      throw err;
    } finally {
      setLoading(false);
    }
  }, [getDomains, setErr, setLoading]);

  const regenerateToken = useCallback(async (domain) => {
    setLoading(true);
    try {
      const response = await axios.post(`/organisation/domains/${domain}/regenerate-token`);
      await getDomains();
      return response.data;
    } catch (err) {
      setErr(err.message || "Failed to regenerate token");
      throw err;
    } finally {
      setLoading(false);
    }
  }, [getDomains, setErr, setLoading]);

  const deleteDomain = useCallback(async (domain) => {
    setLoading(true);
    try {
      await axios.delete(`/organisation/domains/${domain}`);
      await getDomains();
    } catch (err) {
      setErr(err.message || "Failed to delete domain");
      throw err;
    } finally {
      setLoading(false);
    }
  }, [getDomains, setErr, setLoading]);

  return (
    <DomainContext.Provider
      value={{
        ...state,
        getDomains,
        getDomain,
        addDomain,
        verifyDomain,
        regenerateToken,
        deleteDomain,
        setErr,
      }}
    >
      {children}
    </DomainContext.Provider>
  );
}

export { DomainContext, DomainProvider };
